import type * as PreferenceApiTypes from '@dhl/receiver-preference-api'
import type { Address, ProfileAddress } from '@dhl/receiver-profile-api'
import type { ServicepointInputValue } from '@dhl/servicepoint-input'

/**
 * Start of preference API type patches
 *
 * TODO:
 * These custom model types can be removed once the following task has been completed:
 * https://dhlbnl.atlassian.net/browse/CON-7073
 */

type UpdateInterventionTypeBody = {
  interventionType: string
}

type UpdateAgreedPlaceInterventionPreference = PreferenceApiTypes.AgreedPlaceInterventionPreference &
  UpdateInterventionTypeBody
type UpdateServicePointInterventionPreference = PreferenceApiTypes.ServicePointInterventionPreference &
  UpdateInterventionTypeBody

export type InterventionPreference =
  | PreferenceApiTypes.AddressInterventionPreference
  | PreferenceApiTypes.ServicePointInterventionPreference

export interface UpdateInterventionPreference {
  preference: UpdateAgreedPlaceInterventionPreference | UpdateServicePointInterventionPreference
}

export const agreedPlacePermanentlyUnavailable = 'AgreedPlacePermanentlyUnavailable'

export type AgreedPlacePermanentlyUnavailable = PreferenceApiTypes.AgreedPlacePermanentlyUnavailable & {
  cause: typeof agreedPlacePermanentlyUnavailable
}

export const spPermanentlyUnavailable = 'ServicePointPermanentlyUnavailable'

export type ServicePointPermanentlyUnavailable = PreferenceApiTypes.ServicePointPermanentlyUnavailable & {
  cause: typeof spPermanentlyUnavailable
}

export const addressUpdated = 'AddressUpdated'

export type AddressUpdated = PreferenceApiTypes.AddressUpdated & {
  cause: typeof addressUpdated
}

export const spTemporarilyUnavailable = 'ServicePointTemporarilyUnavailable'

export type ServicePointTemporarilyUnavailable = PreferenceApiTypes.ServicePointTemporarilyUnavailable & {
  cause: typeof spTemporarilyUnavailable
}

export type DisableCause =
  | AddressUpdated
  | AgreedPlacePermanentlyUnavailable
  | ServicePointPermanentlyUnavailable
  | ServicePointTemporarilyUnavailable

/**
 * End of preference API type patches
 */

export type AvailablePreferences = 'ringDoorbell' | 'servicePoint' | 'agreedPlace'

export type AgreedPlaceInterventionPreferenceWithPhoto = PreferenceApiTypes.AgreedPlaceInterventionPreference & {
  agreedPlacePhoto?: Blob
}

export type PreferenceFormValues = {
  addressId: string
  ringDoorbell: unknown
  preference: AvailablePreferences | null
  agreedPlace: AgreedPlaceInterventionPreferenceWithPhoto | null
  servicePoint: ServicepointInputValue | null
}

const getEmptyAddress = (): Address => ({
  countryCode: '',
  postalCode: '',
  city: '',
  street: '',
  houseNumber: '',
  houseNumberSuffix: '',
  lines: [],
})

export const getEmptyPreference = (
  profileAddress?: ProfileAddress,
  preferenceOptions?: PreferenceApiTypes.AddressInterventionPreferenceOptions
): PreferenceApiTypes.AddressInterventionPreference => ({
  addressId: profileAddress?.addressId ?? '',
  address: profileAddress?.address ?? getEmptyAddress(),
  addressName: profileAddress?.name ?? '',
  servicePoint: {
    depot: '',
    psfKey: '',
    name: '',
    active: false,
    available: preferenceOptions?.isServicePointAvailable ?? false,
    availableServicePointIds: preferenceOptions?.availableServicePointIds ?? [],
  },
  agreedPlace: {
    description: '',
    active: false,
    available: preferenceOptions?.isAgreedPlaceAvailable ?? false,
    // The open api is not correct from the BE. This will be fix in the next version of the delivery preference.
    ringDoorbell: null as never,
  },
})
